import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";
import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";

import {
  Box,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
} from "@mui/material";

import Page from "./Page";
import Card from "../layout/Card";

const UserExports = () => {
  const { getTitle } = useLocalization();
  const navigate = useNavigate();
  const { addToWorkQueue } = useData();
  const { showMessage } = usePage();
  const { windowHeight } = useBrowser();

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  const [filter, setFilter] = useState({
    isActive: undefined,
    role: undefined,
    language: undefined,
  });

  const handleExportUsers = async () => {
    if (!email) {
      showMessage("User email not found");
      return;
    }

    // Build filter object, removing undefined values
    const exportFilter = {};
    if (filter.isActive !== undefined) exportFilter.isActive = filter.isActive;
    if (filter.role !== undefined) exportFilter.role = filter.role;
    if (filter.language !== undefined) exportFilter.language = filter.language;

    await addToWorkQueue("exportUsers", {
      entityName: "User",
      email,
      userId: currentUserId,
      filter: Object.keys(exportFilter).length > 0 ? exportFilter : undefined,
    });

    showMessage("Your user export will be emailed to you shortly.");
  };

  const handleToolbarClick = (action) => {
    switch (action) {
      case "back":
        navigate("/users");
        break;
    }
  };

  const handleActiveFilterChange = (value) => {
    if (value === "all") {
      setFilter({ ...filter, isActive: undefined });
    } else {
      setFilter({ ...filter, isActive: value === "active" });
    }
  };

  const handleRoleFilterChange = (value) => {
    if (value === "all") {
      setFilter({ ...filter, role: undefined });
    } else {
      setFilter({ ...filter, role: value });
    }
  };

  const handleLanguageFilterChange = (value) => {
    if (value === "all") {
      setFilter({ ...filter, language: undefined });
    } else {
      setFilter({ ...filter, language: value });
    }
  };

  return (
    <Page
      pageName="User Exports"
      includeToolbar={true}
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
        },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        sx={{ display: "flex" }}
        gap={1}
      >
        <Card
          title={getTitle("exports")}
          sx={{
            height: `${windowHeight - 140}px`,
            width: "300px",
          }}
        >
          <Box
            sx={{
              height: `${windowHeight - 200}px`,
              overflow: "auto",
              padding: "8px",
            }}
          >
            <Stack spacing={3}>
              <Box>
                <FormLabel component="legend">Status</FormLabel>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <InputLabel id="user-export-status-label">Status</InputLabel>
                  <Select
                    labelId="user-export-status-label"
                    value={
                      filter.isActive === undefined
                        ? "all"
                        : filter.isActive
                          ? "active"
                          : "inactive"
                    }
                    label="Status"
                    onChange={(e) => handleActiveFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="active">Active Users Only</MenuItem>
                    <MenuItem value="inactive">Inactive Users Only</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FormLabel component="legend">Role</FormLabel>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <InputLabel id="user-export-role-label">Role</InputLabel>
                  <Select
                    labelId="user-export-role-label"
                    value={filter.role || "all"}
                    label="Role"
                    onChange={(e) => handleRoleFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="supervisor">Supervisor</MenuItem>
                    <MenuItem value="rep">Rep</MenuItem>
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="finance">Finance</MenuItem>
                    <MenuItem value="qa">QA</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FormLabel component="legend">Language</FormLabel>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <InputLabel id="user-export-language-label">
                    Language
                  </InputLabel>
                  <Select
                    labelId="user-export-language-label"
                    value={filter.language || "all"}
                    label="Language"
                    onChange={(e) => handleLanguageFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All Languages</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleExportUsers}
                >
                  Export Users
                </Button>
              </Box>
            </Stack>
          </Box>
        </Card>
      </Box>
    </Page>
  );
};

export default UserExports;
